import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useCurrency } from '../../hooks/useCurrency';
import { useLanguage } from '../../hooks/useLanguage';
import NequiBanner from './components/NequiBanner';
import TrustBadges from './components/TrustBadges';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { getDisplayPrice, getSecondaryPrice } from '../../lib/currency';
import HomeSeoJsonLd from './components/SeoJsonLd';

function useSectionAnimation(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { currency } = useCurrency();
  const { t } = useLanguage();
  const { containerRef: winRef, getAnimationClass: winAnim, getAnimationStyle: winStyle } = useScrollAnimation();
  const { containerRef: offRef, getAnimationClass: offAnim, getAnimationStyle: offStyle } = useScrollAnimation();
  const { containerRef: off2Ref, getAnimationClass: off2Anim, getAnimationStyle: off2Style } = useScrollAnimation();
  const { containerRef: avRef, getAnimationClass: avAnim, getAnimationStyle: avStyle } = useScrollAnimation();
  const { containerRef: vpnRef, getAnimationClass: vpnAnim, getAnimationStyle: vpnStyle } = useScrollAnimation();

  const ctaSection = useSectionAnimation();
  const infoSection = useSectionAnimation();
  const trustSection = useSectionAnimation();

  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  const slides = [
    {
      title: t('hero.vpn.title', 'VPN - PROTECCIÓN Y PRIVACIDAD TOTAL'),
      subtitle: t('hero.vpn.subtitle', 'HASTA 77% DE DESCUENTO'),
      description: t('hero.vpn.description', 'Navega seguro y anónimo con las mejores VPN del mercado'),
      buttonText: t('hero.vpn.button', 'Ver ofertas VPN'),
      buttonLink: '/vpn',
      bgColor: 'from-emerald-600 to-teal-600',
      image: 'https://public.readdy.ai/ai/img_res/f75f23f6b883e50f6aeabe8e48e235b4.jpg',
      badge: t('hero.vpn.badge', 'OFERTA -77%'),
    },
    {
      title: t('hero.windows.title', 'LICENCIAS WINDOWS 11'),
      subtitle: t('hero.windows.subtitle', 'Desde $65.963'),
      description: t('hero.windows.description', 'Actualiza tu sistema operativo con las mejores ofertas'),
      buttonText: t('hero.windows.button', 'Comprar ahora'),
      buttonLink: '/windows-11',
      bgColor: 'from-blue-500 to-cyan-500',
      image: 'https://readdy.ai/api/search-image?query=windows%2011%20operating%20system%20modern%20interface%20blue%20gradient%20background%20with%20clean%20desktop%20environment%20floating%20window%20panels%20minimalist%20design%20professional%20technology%20atmosphere%20soft%20ambient%20lighting&width=800&height=500&seq=slide2&orientation=landscape',
    },
    {
      title: t('hero.office.title', 'MICROSOFT OFFICE 365'),
      subtitle: t('hero.office.subtitle', 'MÁS DE 60% DE DESCUENTO'),
      description: t('hero.office.description', 'Productividad profesional al mejor precio'),
      buttonText: t('hero.office.button', 'Ver ofertas'),
      buttonLink: '/microsoft-365',
      bgColor: 'from-orange-500 to-red-500',
      image: 'https://readdy.ai/api/search-image?query=microsoft%20office%20365%20cloud%20productivity%20suite%20vibrant%20orange%20red%20gradient%20background%20with%20floating%20application%20icons%20word%20excel%20powerpoint%20outlook%20teams%20modern%20collaborative%20workspace%20concept%20clean%20professional%20design%20soft%20lighting%20digital%20cloud%20elements&width=800&height=500&seq=slide3office365&orientation=landscape',
      badge: t('hero.office.badge', 'OFERTA +60%'),
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const windowsLicenses = [
    {
      name: 'Licencia Windows 10 PRO Original',
      description: t('product.lifetime'),
      copPrice: 55385,
      copOldPrice: 119900,
      discount: '-54%',
      image: 'https://readdy.ai/api/search-image?query=windows%2010%20pro%20software%20box%20product%20packaging%20blue%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=win1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 287,
    },
    {
      name: 'Licencia Windows 11 PRO Original',
      description: t('product.lifetime'),
      copPrice: 65963,
      copOldPrice: 129900,
      discount: '-49%',
      image: 'https://readdy.ai/api/search-image?query=windows%2011%20pro%20software%20box%20product%20packaging%20modern%20blue%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=win2&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 340,
    },
    {
      name: 'Licencia Windows 11 HOME Original',
      description: t('product.lifetime'),
      copPrice: 79900,
      copOldPrice: 120000,
      discount: '-33%',
      image: 'https://readdy.ai/api/search-image?query=windows%2011%20home%20software%20box%20product%20packaging%20light%20blue%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=win3&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 156,
    },
  ];

  const officeLicenses = [
    {
      id: 'office2026ltsc1v2',
      name: 'Office 365 última versión - 1 cuenta',
      description: t('product.lifetime'),
      copPrice: 149990,
      copOldPrice: 374975,
      discount: '-60%',
      image: 'https://readdy.ai/api/search-image?query=spectacular%20eye%20catching%20Microsoft%20Office%202026%20Professional%20Plus%20premium%20product%20display%20with%20vibrant%20electric%20blue%20orange%20gradient%20holographic%20glow%20effects%20floating%203D%20digital%20license%20card%20featuring%20large%20prominent%20colorful%20Microsoft%20Office%20logo%20with%20Word%20Excel%20PowerPoint%20Outlook%20icons%20in%20bright%20blue%20green%20orange%20colors%20modern%20futuristic%20design%20dramatic%20studio%20lighting%20with%20neon%20accents%20dark%20elegant%20background%20with%20radiant%20light%20beams%20luxury%20software%20packaging%20high%20impact%20commercial%20photography%20attention%20grabbing%20visual&width=400&height=400&seq=off2026ltsc1specialv3&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 72,
    },
    {
      id: 'office2021ltsc5v2',
      name: 'Office 365 última versión - 5 cuentas',
      description: 'Con 5 cuentas, Licencia de por vida con un descuento del 10%',
      copPrice: 149990,
      copOldPrice: 374975,
      discount: '-60%',
      image: 'https://readdy.ai/api/search-image?query=spectacular%20eye%20catching%20Microsoft%20Office%202026%20Professional%20Plus%20premium%20product%20display%20with%20vibrant%20electric%20blue%20orange%20gradient%20holographic%20glow%20effects%20floating%203D%20digital%20license%20card%20pack%20of%20five%20featuring%20large%20prominent%20colorful%20Microsoft%20Office%20logo%20with%20Word%20Excel%20PowerPoint%20Outlook%20icons%20in%20bright%20blue%20green%20orange%20colors%20modern%20futuristic%20design%20dramatic%20studio%20lighting%20with%20neon%20accents%20dark%20elegant%20background%20with%20radiant%20light%20beams%20luxury%20software%20packaging%20high%20impact%20commercial%20photography%20attention%20grabbing%20visual&width=400&height=400&seq=off2026ltsc5specialv3&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 124,
    },
    {
      id: 'office2024pro',
      name: 'Licencia Original Office 2024 Professional Plus',
      description: t('product.lifetime'),
      copPrice: 69900,
      copOldPrice: 229900,
      discount: '-70%',
      image: 'https://readdy.ai/api/search-image?query=vibrant%20modern%20Microsoft%20Office%202024%20Professional%20Plus%20product%20showcase%20with%20glowing%20orange%20red%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20card%20sleek%20futuristic%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20light%20rays%20premium%20software%20packaging%203D%20render%20high%20quality%20commercial%20photography%20attention%20grabbing%20visual&width=400&height=400&seq=offpro2024new1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 59,
    },
    {
      id: 'office2024hb',
      name: 'Licencia Office 2024 Home and Business',
      description: t('product.lifetime'),
      copPrice: 710300,
      copOldPrice: 1250000,
      discount: '-43%',
      image: 'https://readdy.ai/api/search-image?query=stunning%20Microsoft%20Office%202024%20Home%20Business%20premium%20product%20display%20with%20vibrant%20teal%20cyan%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20modern%20minimalist%20design%20dramatic%20lighting%20dark%20elegant%20background%20with%20light%20beams%20luxury%20software%20packaging%203D%20visualization%20high%20end%20commercial%20photography&width=400&height=400&seq=offhb2024new1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 45,
    },
    {
      id: 'office2024ltsc1',
      name: 'Licencia Office 2024 Pro Plus LTSC 1 cuenta',
      description: t('product.lifetime'),
      copPrice: 89900,
      copOldPrice: 229900,
      discount: '-61%',
      image: 'https://readdy.ai/api/search-image?query=striking%20Microsoft%20Office%202024%20LTSC%20Professional%20Plus%20single%20user%20product%20showcase%20with%20bold%20dark%20orange%20amber%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20card%20sleek%20modern%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20warm%20glowing%20accents%20premium%20software%20display%203D%20render%20professional%20commercial%20photography&width=400&height=400&seq=offltsc2024single1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 58,
    },
    {
      id: 'office2024ltsc5',
      name: 'Licencia Office 2024 Pro Plus LTSC 5 cuentas',
      description: 'Con 5 cuentas, licencia de por vida con un descuento del 10%',
      copPrice: 89900,
      copOldPrice: 229900,
      discount: '-61%',
      image: 'https://readdy.ai/api/search-image?query=spectacular%20Microsoft%20Office%202024%20LTSC%20Professional%20Plus%20five%20users%20bundle%20product%20display%20with%20fiery%20red%20orange%20gradient%20holographic%20glow%20floating%20digital%20license%20cards%20pack%20modern%20dynamic%20design%20dramatic%20lighting%20dark%20background%20with%20flame%20like%20light%20effects%20premium%20multi%20user%20software%20packaging%203D%20visualization%20high%20quality%20commercial%20photography&width=400&height=400&seq=offltsc2024new1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      multiplyBy5: true,
      salesCount: 93,
    },
    {
      id: 'office2021pro',
      name: 'Licencia Original Office 2021 Professional Plus',
      description: t('product.lifetime'),
      copPrice: 49950,
      copOldPrice: 199900,
      discount: '-75%',
      image: 'https://readdy.ai/api/search-image?query=impressive%20Microsoft%20Office%202021%20Professional%20Plus%20product%20display%20with%20deep%20blue%20navy%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20sophisticated%20modern%20design%20dramatic%20lighting%20dark%20elegant%20background%20with%20light%20effects%20premium%20software%20packaging%203D%20visualization%20high%20quality%20commercial%20photography&width=400&height=400&seq=offpro2021new1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 32,
    },
    {
      id: 'office2021hb',
      name: 'Licencia Office 2021 Home and Business',
      description: t('product.lifetime'),
      copPrice: 370953,
      copOldPrice: 706563,
      discount: '-47%',
      image: 'https://readdy.ai/api/search-image?query=eye%20catching%20Microsoft%20Office%202021%20Home%20Business%20product%20showcase%20with%20bright%20emerald%20green%20gradient%20holographic%20effects%20floating%20digital%20license%20key%20modern%20sleek%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20glowing%20accents%20premium%20software%20display%203D%20render%20professional%20commercial%20photography&width=400&height=400&seq=offhb2021new1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 34,
    },
    {
      id: 'office2021ltsc1',
      name: 'Licencia Office 2021 Pro Plus LTSC 1 cuenta',
      description: t('product.lifetime'),
      copPrice: 59950,
      copOldPrice: 219900,
      discount: '-73%',
      image: 'https://readdy.ai/api/search-image?query=elegant%20Microsoft%20Office%202021%20LTSC%20Professional%20Plus%20single%20user%20enterprise%20edition%20product%20display%20with%20sophisticated%20silver%20charcoal%20gradient%20holographic%20glow%20floating%20digital%20license%20card%20modern%20corporate%20design%20dramatic%20lighting%20dark%20background%20with%20metallic%20shine%20effects%20premium%20business%20software%20packaging%203D%20visualization%20high%20end%20commercial%20photography&width=400&height=400&seq=offltsc2021single1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 45,
    },
    {
      id: 'office2021ltsc5',
      name: 'Licencia Office 2021 Pro Plus LTSC 5 cuentas',
      description: 'Con 5 cuentas, licencia de por vida con un descuento del 10%',
      copPrice: 59950,
      copOldPrice: 219900,
      discount: '-73%',
      image: 'https://readdy.ai/api/search-image?query=professional%20Microsoft%20Office%202021%20LTSC%20Professional%20Plus%20enterprise%20edition%20product%20showcase%20with%20metallic%20silver%20gray%20gradient%20holographic%20effects%20floating%20digital%20license%20card%20pack%20of%20five%20modern%20corporate%20design%20dramatic%20studio%20lighting%20dark%20background%20with%20chrome%20accents%20premium%20business%20software%20display%203D%20render%20high%20end%20commercial%20photography&width=400&height=400&seq=offltsc2021new1&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      multiplyBy5: true,
      salesCount: 87,
    },
  ];

  const officeLicenses2: any[] = [];

  const antivirusProducts = [
    {
      name: 'Kaspersky Standard 5 Dispositivos - 1 año',
      copPrice: 229450,
      copOldPrice: 550000,
      discount: '-58%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20standard%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 45,
    },
    {
      name: 'Kaspersky Standard 1 Dispositivo - 1 año',
      copPrice: 115600,
      copOldPrice: 195000,
      discount: '-41%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20standard%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2b&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 29,
    },
    {
      name: 'Kaspersky Premium 1 Dispositivo - 1 año',
      copPrice: 173100,
      copOldPrice: 275000,
      discount: '-37%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20premium%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2c&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 62,
    },
    {
      name: 'Kaspersky Premium 5 Dispositivos - 1 año',
      copPrice: 305200,
      copOldPrice: 630000,
      discount: '-52%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20premium%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2d&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 280,
    },
    {
      name: 'Kaspersky Plus 1 Dispositivo - 2 años',
      copPrice: 143750,
      copOldPrice: 275000,
      discount: '-48%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20plus%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av3a&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 67,
    },
    {
      name: 'Kaspersky Plus 5 Dispositivos - 2 años',
      copPrice: 264900,
      copOldPrice: 630000,
      discount: '-58%',
      image: 'https://readdy.ai/api/search-image?query=kaspersky%20plus%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av3b&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 73,
    },
    {
      name: 'McAfee Total Protection 1 Dispositivo - 1 año',
      copPrice: 97750,
      copOldPrice: 180000,
      discount: '-46%',
      image: 'https://readdy.ai/api/search-image?query=mcafee%20total%20protection%20antivirus%20software%20box%20product%20packaging%20red%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av5&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 56,
    },
    {
      name: 'McAfee Total Protection 5 Dispositivos - 1 año',
      copPrice: 281750,
      copOldPrice: 530530,
      discount: '-47%',
      image: 'https://readdy.ai/api/search-image?query=mcafee%20total%20protection%20antivirus%20software%20box%20product%20packaging%20red%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av5b&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 121,
    },
    {
      name: 'ESET NOD32 Antivirus 1 Dispositivo - 2 años',
      copPrice: 109250,
      copOldPrice: 230650,
      discount: '-53%',
      image: 'https://readdy.ai/api/search-image?query=eset%20nod32%20antivirus%20software%20box%20product%20packaging%20orange%20teal%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av6&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 45,
    },
    {
      name: 'ESET NOD32 Antivirus 5 Dispositivos - 2 años',
      copPrice: 339250,
      copOldPrice: 560530,
      discount: '-39%',
      image: 'https://readdy.ai/api/search-image?query=eset%20nod32%20antivirus%20software%20box%20product%20packaging%20orange%20teal%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av6b&orientation=squarish',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 204,
    },
  ];

  const vpnProducts = [
    {
      name: 'AdGuard VPN 1 Mes - 1 Dispositivo',
      copPrice: 30500,
      copOldPrice: 45000,
      discount: '-32%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      badge: t('product.offer'),
      rating: 5,
      salesCount: 78,
    },
    {
      name: 'AdGuard VPN 1 Año - 5 Dispositivos',
      copPrice: 21950,
      copOldPrice: 45000,
      discount: '-51%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      badge: t('product.offer'),
      priceNote: t('product.monthly'),
      multiplyBy: 12,
      rating: 5,
      salesCount: 167,
    },
    {
      name: 'AdGuard VPN 2 Años - 10 Dispositivos',
      copPrice: 14950,
      copOldPrice: 45000,
      discount: '-71%',
      image: 'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
      badge: t('product.offer'),
      priceNote: t('product.monthly'),
      multiplyBy: 24,
      rating: 5,
      salesCount: 283,
    },
  ];

  const filterProducts = (products: any[]) => {
    if (!searchQuery.trim()) return products;
    const query = searchQuery.toLowerCase();
    return products.filter(product => 
      product.name.toLowerCase().includes(query) ||
      (product.description && product.description.toLowerCase().includes(query))
    );
  };

  const filteredWindowsLicenses = filterProducts(windowsLicenses);
  const filteredOfficeLicenses = filterProducts(officeLicenses);
  const filteredOfficeLicenses2 = filterProducts(officeLicenses2);
  const filteredAntivirusProducts = filterProducts(antivirusProducts);
  const filteredVpnProducts = filterProducts(vpnProducts);

  const hasResults = filteredWindowsLicenses.length > 0 || 
                     filteredOfficeLicenses.length > 0 || 
                     filteredOfficeLicenses2.length > 0 || 
                     filteredAntivirusProducts.length > 0 || 
                     filteredVpnProducts.length > 0;

  const handleProductClick = (product: any) => {
    const priceNumber = product.copPrice;
    
    if (product.multiplyBy5) {
      const totalPrice = priceNumber * 5;
      const oldPriceNumber = product.copOldPrice || 0;
      const totalOldPrice = oldPriceNumber * 5;
      const savings = totalOldPrice - totalPrice;
      
      addItem({
        id: product.id || product.name.toLowerCase().replace(/\s+/g, '-'),
        name: product.name,
        price: totalPrice,
        oldPrice: totalOldPrice > 0 ? totalOldPrice : undefined,
        image: product.image,
        type: 'Licencia Digital - Pack 5 cuentas',
        savingsDescription: totalOldPrice > 0 ? `Precio original 5 cuentas: $${totalOldPrice.toLocaleString('es-CO')} \u2014 Ahorras: $${savings.toLocaleString('es-CO')}` : undefined,
      });
      navigate('/carrito');
      return;
    }
    
    if (product.multiplyBy) {
      const finalPrice = priceNumber * product.multiplyBy;
      addItem({
        id: product.name.toLowerCase().replace(/\s+/g, '-'),
        name: product.name,
        price: finalPrice,
        image: product.image,
        type: 'Licencia Digital',
      });
      navigate('/carrito');
      return;
    }
    
    addItem({
      id: product.name.toLowerCase().replace(/\s+/g, '-'),
      name: product.name,
      price: priceNumber,
      image: product.image,
      type: 'Licencia Digital',
    });
    navigate('/carrito');
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <HomeSeoJsonLd />
      <Header onSearch={handleSearch} />

      {/* Hero Carousel */}
      {!searchQuery && (
        <section className="relative w-full h-[280px] sm:h-[380px] md:h-[450px] lg:h-[500px] overflow-hidden">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${slide.bgColor}`}>
                <div className="absolute inset-0 bg-black/20"></div>
              </div>
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover opacity-30"
                />
              </div>
              <div className="relative container mx-auto px-4 h-full flex items-center">
                <div className="max-w-2xl text-white">
                  {slide.badge && (
                    <div className="inline-block bg-red-600 text-white px-2.5 py-1 sm:px-4 sm:py-2 rounded-full font-bold text-xs sm:text-sm md:text-lg mb-2 sm:mb-4 shadow-lg">
                      <i className="ri-percent-line mr-1"></i>
                      {slide.badge}
                    </div>
                  )}
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold mb-1.5 sm:mb-3 md:mb-4 leading-tight">{slide.title}</h2>
                  <p className="text-base sm:text-lg md:text-2xl lg:text-3xl font-semibold mb-1.5 sm:mb-3 md:mb-4">{slide.subtitle}</p>
                  <p className="text-xs sm:text-sm md:text-lg lg:text-xl mb-3 sm:mb-6 md:mb-8 opacity-90">{slide.description}</p>
                  <Link
                    to={slide.buttonLink}
                    className="inline-block bg-white text-gray-900 px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-3 rounded-lg text-xs sm:text-sm md:text-base font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {slide.buttonText}
                  </Link>
                </div>
              </div>
            </div>
          ))}

          <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all cursor-pointer ${
                  index === currentSlide ? 'bg-white w-6 sm:w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg sm:text-2xl"></i>
          </button>
          <button
            onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg sm:text-2xl"></i>
          </button>
        </section>
      )}

      {!searchQuery && <NequiBanner />}

      <main className="flex-1">
        {searchQuery && (
          <div className="py-6 sm:py-8 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 line-clamp-1">
                  {t('home.search_results')}: &quot;{searchQuery}&quot;
                </h2>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-[#0078D4] hover:text-[#005a9e] font-medium cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-close-line mr-1"></i>
                  {t('home.clear')}
                </button>
              </div>
            </div>
          </div>
        )}

        {searchQuery && !hasResults && (
          <div className="py-12 sm:py-16 bg-white">
            <div className="container mx-auto px-4 text-center">
              <i className="ri-search-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{t('home.no_products')}</h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                {t('home.no_match')} &quot;{searchQuery}&quot;
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="bg-[#0078D4] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#005a9e] transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                {t('home.view_all')}
              </button>
            </div>
          </div>
        )}

        {/* Windows Licenses */}
        {filteredWindowsLicenses.length > 0 && (
          <section className="py-8 sm:py-12 lg:py-16 bg-white" data-product-shop>
            <div className="container mx-auto px-3 sm:px-4">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8">{t('home.windows_licenses')}</h2>
              <div ref={winRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                {filteredWindowsLicenses.map((product, index) => {
                  const display = getDisplayPrice(product.copPrice, product.copOldPrice, currency);
                  const secondary = getSecondaryPrice(product.copPrice, product.copOldPrice, currency);
                  return (
                    <div 
                      key={index}
                      data-anim-index={index}
                      style={winStyle(index)}
                      className={`bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-500 ease-out group cursor-pointer ${winAnim(index)}`}
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="relative">
                        {product.badge && (
                          <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">
                            {product.badge}
                          </span>
                        )}
                        {product.discount && (
                          <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">
                            {product.discount}
                          </span>
                        )}
                        <div className="w-full h-32 sm:h-40 lg:h-48 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                          <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                        </div>
                      </div>
                      <div className="p-2.5 sm:p-3 lg:p-4">
                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 h-8 sm:h-10">{product.name}</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                          <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{display.main}</span>
                          {display.oldMain && <span className="text-[10px] sm:text-sm text-gray-500 line-through">{display.oldMain}</span>}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 mt-0.5">
                          <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">≈ {secondary.current}</p>
                          {secondary.old && <p className="text-[10px] sm:text-xs text-gray-400 line-through">{secondary.old}</p>}
                        </div>
                        <div className="flex items-center gap-1 mb-1 sm:mb-1.5">
                          <div className="flex gap-0.5">
                            {[...Array(product.rating)].map((_, i) => (
                              <i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>
                            ))}
                          </div>
                          <span className="text-[10px] sm:text-xs text-gray-500">(5.0)</span>
                        </div>
                        <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                          <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                          <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} {t('product.sold')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Office Licenses */}
        {filteredOfficeLicenses.length > 0 && (
          <section className="py-8 sm:py-12 lg:py-16 bg-gray-50" data-product-shop>
            <div className="container mx-auto px-3 sm:px-4">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8">{t('home.office_licenses')}</h2>
              <div ref={offRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {filteredOfficeLicenses.map((product, index) => {
                  const display = getDisplayPrice(product.copPrice, product.copOldPrice, currency);
                  const secondary = getSecondaryPrice(product.copPrice, product.copOldPrice, currency);
                  return (
                    <div 
                      key={index}
                      data-anim-index={index}
                      style={offStyle(index)}
                      className={`bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-500 ease-out group cursor-pointer ${offAnim(index)}`}
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="relative">
                        {product.badge && <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.badge}</span>}
                        {product.discount && <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.discount}</span>}
                        <div className="w-full h-32 sm:h-40 lg:h-48 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                          <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                        </div>
                      </div>
                      <div className="p-2.5 sm:p-3 lg:p-4">
                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 h-8 sm:h-10">{product.name}</h3>
                        {product.description && <p className="text-[10px] sm:text-xs text-emerald-600 font-semibold mb-1.5 sm:mb-2 line-clamp-1 sm:line-clamp-2">{product.description}</p>}
                        <div className="flex items-center gap-1 mb-1 sm:mb-1.5">
                          <div className="flex gap-0.5">{[...Array(product.rating)].map((_, i) => (<i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>))}</div>
                          <span className="text-[10px] sm:text-xs text-gray-500">(5.0)</span>
                        </div>
                        <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                          <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                          <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} {t('product.sold')}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                          <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{display.main}</span>
                          {display.oldMain && <span className="text-[10px] sm:text-sm text-gray-500 line-through">{display.oldMain}</span>}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 mt-0.5">
                          <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">≈ {secondary.current}</p>
                          {secondary.old && <p className="text-[10px] sm:text-xs text-gray-400 line-through">{secondary.old}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Office Licenses 2 */}
        {filteredOfficeLicenses2.length > 0 && (
          <section className="py-8 sm:py-12 lg:py-16 bg-white" data-product-shop>
            <div className="container mx-auto px-3 sm:px-4">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8">{t('home.office_ltsc')}</h2>
              <div ref={off2Ref} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                {filteredOfficeLicenses2.map((product, index) => {
                  const display = getDisplayPrice(product.copPrice, product.copOldPrice, currency);
                  const secondary = getSecondaryPrice(product.copPrice, product.copOldPrice, currency);
                  return (
                    <div 
                      key={index}
                      data-anim-index={index}
                      style={off2Style(index)}
                      className={`bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-500 ease-out group cursor-pointer ${off2Anim(index)}`}
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="relative">
                        {product.badge && <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.badge}</span>}
                        {product.discount && <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.discount}</span>}
                        <div className="w-full h-32 sm:h-40 lg:h-48 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                          <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                        </div>
                      </div>
                      <div className="p-2.5 sm:p-3 lg:p-4">
                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1.5 sm:mb-2 line-clamp-2 h-8 sm:h-10">{product.name}</h3>
                        {product.description && <p className="text-[10px] sm:text-xs text-emerald-600 font-semibold mb-1.5 sm:mb-2 line-clamp-1 sm:line-clamp-2">{product.description}</p>}
                        <div className="flex items-center gap-1 mb-1 sm:mb-1.5">
                          <div className="flex gap-0.5">{[...Array(product.rating)].map((_, i) => (<i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>))}</div>
                          <span className="text-[10px] sm:text-xs text-gray-500">(5.0)</span>
                        </div>
                        <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                          <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                          <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} {t('product.sold')}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                          <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{display.main}</span>
                          {display.oldMain && <span className="text-[10px] sm:text-sm text-gray-500 line-through">{display.oldMain}</span>}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 mt-0.5">
                          <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">≈ {secondary.current}</p>
                          {secondary.old && <p className="text-[10px] sm:text-xs text-gray-400 line-through">{secondary.old}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Antivirus */}
        {filteredAntivirusProducts.length > 0 && (
          <section className="py-8 sm:py-12 lg:py-16 bg-white" data-product-shop>
            <div className="container mx-auto px-3 sm:px-4">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8">{t('home.antivirus')}</h2>
              <div ref={avRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                {filteredAntivirusProducts.map((product, index) => {
                  const display = getDisplayPrice(product.copPrice, product.copOldPrice, currency);
                  const secondary = getSecondaryPrice(product.copPrice, product.copOldPrice, currency);
                  return (
                    <div 
                      key={index}
                      data-anim-index={index}
                      style={avStyle(index)}
                      className={`bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-500 ease-out group cursor-pointer ${avAnim(index)}`}
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="relative">
                        {product.badge && <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.badge}</span>}
                        {product.discount && <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.discount}</span>}
                        <div className="w-full h-32 sm:h-40 lg:h-48 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                          <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                        </div>
                      </div>
                      <div className="p-2.5 sm:p-3 lg:p-4">
                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 h-8 sm:h-10">{product.name}</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                          <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{display.main}</span>
                          {display.oldMain && <span className="text-[10px] sm:text-sm text-gray-500 line-through">{display.oldMain}</span>}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 mt-0.5">
                          <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">≈ {secondary.current}</p>
                          {secondary.old && <p className="text-[10px] sm:text-xs text-gray-400 line-through">{secondary.old}</p>}
                        </div>
                        <div className="flex items-center gap-1 mb-1 sm:mb-1.5">
                          <div className="flex gap-0.5">{[...Array(product.rating)].map((_, i) => (<i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>))}</div>
                          <span className="text-[10px] sm:text-xs text-gray-500">(5.0)</span>
                        </div>
                        <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                          <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                          <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} {t('product.sold')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* VPN */}
        {filteredVpnProducts.length > 0 && (
          <section className="py-8 sm:py-12 lg:py-16 bg-gray-50" data-product-shop>
            <div className="container mx-auto px-3 sm:px-4">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8">{t('home.vpn')}</h2>
              <div ref={vpnRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                {filteredVpnProducts.map((product, index) => {
                  const display = getDisplayPrice(product.copPrice, product.copOldPrice, currency);
                  const secondary = getSecondaryPrice(product.copPrice, product.copOldPrice, currency);
                  const isBestOption = index === 2;
                  return (
                    <div 
                      key={index}
                      data-anim-index={index}
                      style={vpnStyle(index)}
                      className={`bg-white rounded-lg overflow-hidden hover:shadow-lg transition-all duration-500 ease-out group cursor-pointer ${vpnAnim(index)} ${
                        isBestOption 
                          ? 'border-4 border-teal-500' 
                          : 'border-4 border-gray-200 hover:border-teal-500'
                      }`}
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="relative">
                        {isBestOption ? (
                          <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-teal-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">
                            Mejor Opción
                          </span>
                        ) : (
                          product.badge && <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.badge}</span>
                        )}
                        {product.discount && <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">{product.discount}</span>}
                        <div className="w-full h-32 sm:h-40 lg:h-48 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                          <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                        </div>
                      </div>
                      <div className="p-2.5 sm:p-3 lg:p-4">
                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 h-8 sm:h-10">{product.name}</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                          <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{display.main}</span>
                          {display.oldMain && <span className="text-[10px] sm:text-sm text-gray-500 line-through">{display.oldMain}</span>}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 mt-0.5">
                          <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">≈ {secondary.current}</p>
                          {secondary.old && <p className="text-[10px] sm:text-xs text-gray-400 line-through">{secondary.old}</p>}
                        </div>
                        {product.priceNote && <p className="text-[10px] sm:text-xs text-gray-600 mt-1">{product.priceNote}</p>}
                        <div className="flex items-center gap-1 mb-1 sm:mb-1.5">
                          <div className="flex gap-0.5">{[...Array(product.rating)].map((_, i) => (<i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>))}</div>
                          <span className="text-[10px] sm:text-xs text-gray-500">(5.0)</span>
                        </div>
                        <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                          <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                          <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} {t('product.sold')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* CTA + Info + Trust */}
        {!searchQuery && (
          <>
            <section
              ref={ctaSection.ref}
              className={`py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-purple-600 to-blue-600 relative overflow-hidden transition-all duration-1000 ease-out ${ctaSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            >
              <div className="absolute inset-0">
                <img src="https://readdy.ai/api/search-image?query=modern%20technology%20business%20office%20workspace%20with%20computers%20and%20digital%20devices%20professional%20environment%20soft%20purple%20blue%20lighting%20abstract%20background%20clean%20minimalist%20design&width=1920&height=400&seq=ctabg1&orientation=landscape" alt="Background" className="w-full h-full object-cover opacity-20" />
              </div>
              <div className="relative container mx-auto px-4 text-center text-white">
                <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 transition-all duration-700 delay-200 ${ctaSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>{t('home.store_or_reseller')}</h2>
                <p className={`text-sm sm:text-base lg:text-xl mb-6 sm:mb-8 max-w-3xl mx-auto transition-all duration-700 delay-300 ${ctaSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                  {t('home.store_desc')}
                </p>
                <Link
                  to="/distribuidor"
                  className={`inline-flex items-center gap-2 bg-white text-purple-600 px-5 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-lg cursor-pointer whitespace-nowrap mb-6 sm:mb-8 text-sm sm:text-base duration-700 delay-[400ms] ${ctaSection.visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                >
                  <i className="ri-user-star-line"></i>
                  {t('home.become_distributor')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm transition-all duration-700 delay-500 ${ctaSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                  <div className="flex items-center gap-2">
                    <i className="ri-check-double-line text-xl sm:text-2xl"></i>
                    <span>{t('home.special_prices')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-vip-crown-line text-xl sm:text-2xl"></i>
                    <span>{t('home.exclusive_conditions')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-customer-service-2-line text-xl sm:text-2xl"></i>
                    <span>{t('home.priority_support')}</span>
                  </div>
                </div>
              </div>
            </section>

            <section ref={infoSection.ref} className="py-10 sm:py-12 lg:py-16 bg-white">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
                  {[
                    { icon: 'ri-price-tag-3-line', title: t('home.lowest_prices'), desc: t('home.lowest_prices_desc') },
                    { icon: 'ri-shield-check-line', title: t('home.fast_shipping'), desc: t('home.fast_shipping_desc') },
                    { icon: 'ri-customer-service-2-line', title: t('home.guaranteed'), desc: t('home.guaranteed_desc') },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className={`text-center transition-all duration-700 ease-out ${infoSection.visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'}`}
                      style={{ transitionDelay: `${index * 150 + 100}ms` }}
                    >
                      <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-[#0078D4] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 transition-all duration-700 ${infoSection.visible ? 'scale-100 rotate-0' : 'scale-50 rotate-[-15deg]'}`} style={{ transitionDelay: `${index * 150 + 200}ms` }}>
                        <i className={`${item.icon} text-white text-xl sm:text-2xl`}></i>
                      </div>
                      <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <div
              ref={trustSection.ref}
              className={`transition-all duration-1000 ease-out ${trustSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            >
              <TrustBadges />
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
